import { Col } from 'antd';
import {
  ArrayFormFieldsRecord,
  FormFieldsRecord,
} from '../../helpers/formHelpers/types';
import {
  DatePicker,
  Editor,
  ImageUpload,
  InputNumber,
  InputText,
  Select,
  TextArea,
} from '../common/form';
import { getIn, useFormikContext } from 'formik';

type BaseProps = {
  formFields: FormFieldsRecord | ArrayFormFieldsRecord;
  disableFormFields: boolean;
};

type ArrayFormFieldsProps = BaseProps & {
  name: string;
  index: number;
};

type Props = BaseProps | ArrayFormFieldsProps;

// form fields based on their input types
const FormFields = ({ formFields, disableFormFields, ...restProps }: Props) => {
  const { setFieldValue, values } = useFormikContext();

  return (
    <>
      {Object.values(formFields).map(field => {
        const fieldKey =
          'name' in restProps && 'index' in restProps
            ? // for array form fields
              `${restProps.name}.${restProps.index}.${field.key}`
            : // for basic form fields
              field.key;

        if (field.type === 'select') {
          return (
            <Col key={field.key} span={field.col}>
              <Select
                name={fieldKey}
                label={`${field.label}${field.required ? ' *' : ''}`}
                placeholder={field.placeholder}
                bordered
                options={field.options}
                allowClear={false}
                disabled={disableFormFields}
                mode={field.mode}
              />
            </Col>
          );
        } else if (field.type === 'datepicker') {
          return (
            <Col span={field.col} key={field.key}>
              <DatePicker
                key={fieldKey}
                name={fieldKey}
                label={`${field.label}${field.required ? ' *' : ''}`}
                disabledDate={field.disabledDate}
                allowClear={false}
                disabled={disableFormFields}
                placeholder={field.placeholder}
              />
            </Col>
          );
        } else if (field.type === 'editor') {
          return (
            <Col span={24} key={field.key}>
              <Editor
                name={fieldKey}
                label={`${field.label}${field.required ? ' *' : ''}`}
                disabled={disableFormFields}
              />
            </Col>
          );
        } else if (field.type === 'image') {
          return (
            <Col span={24} key={field.key}>
              <ImageUpload
                name={fieldKey}
                label={`${field.label}${field.required ? ' *' : ''}`}
                accept={'.jpg, .jpeg, .png'}
                disabled={disableFormFields}
                maxCount={field.maxCount}
                onRemove={removedFile => {
                  const uploadedFilesList = getIn(values, fieldKey);
                  const updatedFilesList = uploadedFilesList.filter(
                    (file: typeof removedFile) => file.uid !== removedFile.uid,
                  );
                  setFieldValue(fieldKey, updatedFilesList);
                  // return false to not show the previous image value
                  return false;
                }}
              />
            </Col>
          );
        } else if (field.type === 'number') {
          return (
            <Col span={field.col} key={field.key}>
              <InputNumber
                name={fieldKey}
                label={`${field.label}${field.required ? ' *' : ''}`}
                bordered
                disabled={disableFormFields}
                placeholder={field.placeholder}
                addonBefore={field.addonBefore}
                precision={field.precision}
              />
            </Col>
          );
        } else if (field.type === 'text') {
          return (
            <Col span={field.col} key={field.key}>
              <InputText
                name={fieldKey}
                label={`${field.label}${field.required ? ' *' : ''}`}
                placeholder={field.placeholder}
                allowClear
                bordered
                disabled={disableFormFields}
              />
            </Col>
          );
        } else if (field.type === 'textarea') {
          return (
            <Col span={24} key={field.key}>
              <TextArea
                name={fieldKey}
                label={`${field.label}${field.required ? ' *' : ''}`}
                disabled={disableFormFields}
                rows={field.rows}
                autoSize={field.autoSize}
                showCount={field.showCount}
                onKeyDown={field.onKeyDown}
              />
            </Col>
          );
        }
        return <></>;
      })}
    </>
  );
};

export default FormFields;
